import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { X, Battery, Heart, Copy, Check, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pre-loaded flare day scripts
const FLARE_SCRIPTS = [
  {
    id: 'cancel-plans',
    title: "Can't make it today",
    script: "I'm so sorry, but I'm not feeling well today and need to cancel. I was really looking forward to seeing you. Can we reschedule for another time? Thank you for understanding."
  },
  {
    id: 'need-rest',
    title: 'Need to rest',
    script: "I need to take some time to rest right now. My body is telling me to slow down. I'll be back when I'm feeling better."
  },
  {
    id: 'work-sick',
    title: 'Calling in sick',
    script: "Hi, I'm not feeling well today and won't be able to come in. I'll keep you updated on when I expect to return. Thank you for understanding."
  },
  {
    id: 'boundary',
    title: 'Setting a boundary',
    script: "I appreciate you thinking of me, but I'm not able to do that right now. My energy is very limited today and I need to conserve it."
  }
];

function QuickScript({ script, onCopy }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(script.script);
    setCopied(true);
    onCopy();
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <button
      onClick={handleCopy}
      className="w-full text-left card-soft p-4 hover:border-primary/30 transition-all"
      data-testid={`script-${script.id}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-foreground">{script.title}</span>
        {copied ? (
          <Check className="h-5 w-5 text-green-500" />
        ) : (
          <Copy className="h-5 w-5 text-muted-foreground" />
        )}
      </div>
      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{script.script}</p>
    </button>
  );
}

function EnergyButton({ level, selected, onClick, label, color }) {
  return (
    <button
      onClick={() => onClick(level)}
      className={`flex-1 p-4 rounded-2xl text-center transition-all ${
        selected === level 
          ? `${color} text-white shadow-lg scale-105` 
          : 'bg-secondary hover:bg-secondary/80'
      }`}
      data-testid={`energy-${level}`}
    >
      <div className="text-2xl font-bold">{level}</div>
      <div className="text-xs mt-1">{label}</div>
    </button>
  );
}

export default function FlareMode({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [energy, setEnergy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showScripts, setShowScripts] = useState(false);

  const handleQuickLog = async () => {
    if (!energy) {
      toast.error('Please select your energy level');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/symptoms`, {
        energy_level: energy,
        pain_level: energy <= 3 ? 7 : 5,
        mood_level: energy <= 2 ? 3 : 5,
        symptoms: energy <= 3 ? ['Fatigue', 'Brain fog'] : ['Fatigue'],
        notes: 'Logged via Flare Mode'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Logged! Take care of yourself today 💜');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleScriptCopy = () => {
    toast.success('Copied to clipboard');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-y-auto" data-testid="flare-mode">
      <div className="min-h-screen pb-safe">
        <div className="max-w-lg mx-auto px-6 pt-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-2xl font-semibold text-foreground">Flare Day Mode</h1>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded-full" data-testid="close-flare-mode">
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <p className="mt-2 text-muted-foreground">Simplified tools for low-energy days</p>

          {/* Quick Energy Check */}
          <div className="mt-8 card-soft bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200/50">
            <div className="flex items-center gap-2 mb-4">
              <Battery className="h-5 w-5 text-purple-500" />
              <span className="font-medium text-foreground">How's your energy?</span>
            </div>
            
            <div className="flex gap-2">
              <EnergyButton level={1} selected={energy} onClick={setEnergy} label="Empty" color="bg-red-500" />
              <EnergyButton level={3} selected={energy} onClick={setEnergy} label="Low" color="bg-orange-500" />
              <EnergyButton level={5} selected={energy} onClick={setEnergy} label="Okay" color="bg-yellow-500" />
              <EnergyButton level={7} selected={energy} onClick={setEnergy} label="Good" color="bg-green-500" />
            </div>
            
            <Button 
              onClick={handleQuickLog} 
              disabled={saving || !energy} 
              className="w-full mt-4 btn-primary"
              data-testid="quick-log-btn"
            >
              {saving ? 'Saving...' : 'Quick Log'}
            </Button>
          </div>

          {/* Quick Scripts */}
          <div className="mt-6">
            <button
              onClick={() => setShowScripts(!showScripts)}
              className="w-full flex items-center justify-between p-4 card-soft"
              data-testid="toggle-scripts"
            >
              <div className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-blue-500" />
                <span className="font-medium text-foreground">Quick Scripts</span>
              </div>
              <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${showScripts ? 'rotate-90' : ''}`} />
            </button>
            
            {showScripts && (
              <div className="mt-3 space-y-3">
                {FLARE_SCRIPTS.map(script => (
                  <QuickScript key={script.id} script={script} onCopy={handleScriptCopy} />
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="mt-6 space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Quick Actions</p>
            
            <button
              onClick={() => { onClose(); navigate('/skills/emotional'); }}
              className="w-full card-soft p-4 flex items-center gap-3 hover:border-primary/30"
              data-testid="breathing-exercise"
            >
              <Heart className="h-5 w-5 text-rose-500" />
              <div className="text-left">
                <p className="font-medium text-foreground">2-Min Breathing</p>
                <p className="text-xs text-muted-foreground">Quick calm-down exercise</p>
              </div>
            </button>
            
            <button
              onClick={() => { onClose(); navigate('/journal'); }}
              className="w-full card-soft p-4 flex items-center gap-3 hover:border-primary/30"
              data-testid="journal-link"
            >
              <div className="h-5 w-5 rounded bg-amber-100 flex items-center justify-center text-amber-600 text-xs">📝</div>
              <div className="text-left">
                <p className="font-medium text-foreground">Quick Journal</p>
                <p className="text-xs text-muted-foreground">Write a few words</p>
              </div>
            </button>
          </div>

          {/* Gentle Reminder */}
          <div className="mt-8 p-4 bg-purple-50 rounded-2xl border border-purple-100">
            <p className="text-sm text-purple-800 text-center">
              💜 It's okay to rest. You're doing your best.
            </p>
          </div>

          <div className="h-8" />
        </div>
      </div>
    </div>
  );
}
